import React, { Fragment, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { H5, Breadcrumbs } from "../../AbstractElements";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  FormGroup,
  Input,
  Label,
  Row,
} from "reactstrap";
import FooterCard from "../../Components/Forms/FormControl/Common/FooterCard";
import { titleCase } from "../../utils/case";
import { createUser } from "../../api/user.api";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

const UserCreate = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const formRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [togglePassword, setTogglePassword] = useState(false);

  const [form, setForm] = useState({
    name: "",
    username: "",
    email: "",
    password: "",
    role: "",
  });

  const generateUsername = (name) => {
    if (!name) return "";

    const parts = name.trim().toLowerCase().split(/\s+/);

    if (parts.length === 1) return parts[0];

    return parts[0] + parts[1].charAt(0);
  };

  const handleChange = (field, value) => {
    setForm((prev) => {
      const updated = {
        ...prev,
        [field]: value,
      };

      if (field === "name") {
        updated.username = generateUsername(value);
      }

      return updated;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (
        !form.name ||
        !form.username ||
        !form.email ||
        !form.password ||
        !form.role
      ) {
        return toast.error("all fields are required");
      }

      setLoading(true);

      await createUser(form);

      toast.success(t("user created successfully"));

      navigate("/users");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  const triggerSubmit = () => {
    if (formRef.current) {
      formRef.current.requestSubmit();
    }
  };

  return (
    <Fragment>
      <Breadcrumbs
        parent={titleCase(t("user"))}
        title={titleCase(t("new user"))}
        mainTitle={titleCase(t("user management"))}
      />

      <Container fluid>
        <Row>
          <Col>
            <Card className="mb-4">
              <CardHeader>
                <H5>{titleCase(t("new user"))}</H5>
              </CardHeader>

              <CardBody>
                <Form innerRef={formRef} onSubmit={handleSubmit}>
                  <FormGroup>
                    <Label>{titleCase(t("name"))}</Label>
                    <Input
                      type="text"
                      value={form.name}
                      onChange={(e) =>
                        handleChange("name", e.target.value)
                      }
                      required
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>{titleCase(t("username"))}</Label>
                    <Input
                      type="text"
                      value={form.username}
                      onChange={(e) =>
                        handleChange("username", e.target.value)
                      }
                      required
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>{titleCase(t("email"))}</Label>
                    <Input
                      type="email"
                      value={form.email}
                      onChange={(e) =>
                        handleChange("email", e.target.value)
                      }
                      required
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>{titleCase(t("password"))}</Label>

                    <div style={{ position: "relative" }}>
                      <Input
                        type={togglePassword ? "text" : "password"}
                        value={form.password}
                        onChange={(e) =>
                          handleChange("password", e.target.value)
                        }
                        required
                        style={{ paddingRight: "40px" }}
                      />

                      <span
                        onClick={() => setTogglePassword(!togglePassword)}
                        style={{
                          position: "absolute",
                          right: "10px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          cursor: "pointer",
                          fontSize: "12px",
                          userSelect: "none",
                        }}
                        className="text-primary"
                      >
                        {togglePassword ? "hide" : "show"}
                      </span>
                    </div>
                  </FormGroup>

                  <FormGroup>
                    <Label>{titleCase(t("role"))}</Label>
                    <Input
                      type="select"
                      value={form.role}
                      onChange={(e) =>
                        handleChange("role", e.target.value)
                      }
                      required
                    >
                      <option value="">
                        {titleCase(t("select role"))}
                      </option>
                      <option value="superadmin">
                        {titleCase(t("superadmin"))}
                      </option>
                      <option value="staff">
                        {titleCase(t("staff"))}
                      </option>
                    </Input>
                  </FormGroup>
                </Form>
              </CardBody>
            </Card>

            <FooterCard
              onSubmit={triggerSubmit}
              onCancel={handleCancel}
              loading={loading}
            />
          </Col>
        </Row>
      </Container>
    </Fragment>
  );
};

export default UserCreate;